import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { defer, from, map, Observable } from 'rxjs';

import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Color from '@arcgis/core/Color';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import * as symbolJsonUtils from '@arcgis/core/symbols/support/jsonUtils';

import { AppConfigService } from './app-config.service';
import { CacheFactoryService, ICacheService } from './cache-factory.service';

import { ColorUtils } from '../utils/color.utils';
import { EsriUtils } from '../utils/esri.utils';

export enum DrawType {
  Arrow = 'arrow',
  Circle = 'circle',
  Ellipse = 'ellipse',
  Point = 'point',
  Polygon = 'polygon',
  Polyline = 'polyline',
  Rectangle = 'rectangle',
  Text = 'text',
  Triangle = 'triangle',
}

export enum EditorType {
  Point,
  Line,
  Default,
  Text,
}

export type SymbolType =
  | __esri.SimpleMarkerSymbol
  | __esri.SimpleFillSymbol
  | __esri.SimpleLineSymbol
  | __esri.TextSymbol;

@Injectable()
export class NotesSymbolService {
  private cacheService: ICacheService;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    cacheFactory: CacheFactoryService
  ) {
    this.cacheService = cacheFactory.getOrCreate('symbols');
  }

  public createSymbolByDrawType(
    drawingType: DrawType,
    backgroundColorSymbol: string,
    outlineColorSymbol: string,
    opacitySymbol: number,
    outlineWidthSymbol: number,
    widthSymbol: number,
    phraseSymbol: string,
    fontColorSymbol: string,
    fontSizeSymbol: string,
    styleLineSymbol: string,
    symbolPreview: SymbolType
  ) {
    let symbol: __esri.Symbol;
    switch (drawingType) {
      case DrawType.Polygon:
      case DrawType.Circle:
      case DrawType.Rectangle:
      case DrawType.Triangle:
      case DrawType.Arrow:
        symbol = this.createPolygonSymbol(
          backgroundColorSymbol,
          outlineColorSymbol,
          opacitySymbol,
          outlineWidthSymbol
        );
        break;
      case DrawType.Point:
        symbol = this.createPointSymbol(
          widthSymbol,
          backgroundColorSymbol,
          opacitySymbol,
          symbolPreview as
            | __esri.SimpleMarkerSymbol
            | __esri.PictureMarkerSymbol
        );
        break;
      case DrawType.Polyline:
        symbol = this.createPolylineSymbol(
          widthSymbol,
          backgroundColorSymbol,
          opacitySymbol,
          styleLineSymbol
        );
        break;
      case DrawType.Text:
        symbol = this.createTextSymbol(
          phraseSymbol,
          fontColorSymbol,
          fontSizeSymbol,
          opacitySymbol
        );
        break;
      case DrawType.Ellipse:
        symbol = this.createEllipseSymbol(
          backgroundColorSymbol,
          outlineColorSymbol,
          opacitySymbol,
          outlineWidthSymbol
        );
        break;
    }

    return symbol;
  }

  public createPointSymbol(
    widthSymbol: number,
    backgroundColorSymbol: string,
    opacitySymbol: number,
    symbolPreview: __esri.SimpleMarkerSymbol | __esri.PictureMarkerSymbol
  ) {
    const backgroundColor = ColorUtils.alpha(
      backgroundColorSymbol,
      opacitySymbol
    ).rgba();

    const pointSymbol = symbolPreview
      ? symbolPreview.clone()
      : new SimpleMarkerSymbol();

    if (pointSymbol.type === 'simple-marker') {
      pointSymbol.size = widthSymbol;
      pointSymbol.outline.color = new Color(backgroundColor);
      pointSymbol.color = new Color(backgroundColor);
    }

    return pointSymbol;
  }

  public createPolygonSymbol(
    backgroundColorSymbol: string,
    outlineColorSymbol: string,
    opacitySymbol: number,
    outlineWidthSymbol: number,
    styleLineSymbol: any = 'solid'
  ) {
    const outlineColor = ColorUtils.alpha(
      outlineColorSymbol,
      opacitySymbol
    ).rgba();
    const backgroundColor = ColorUtils.alpha(
      backgroundColorSymbol,
      opacitySymbol
    ).rgba();

    const symbol = new SimpleFillSymbol({
      color: backgroundColor,
      outline: {
        color: outlineColor,
        width: outlineWidthSymbol,
        style: styleLineSymbol,
      },
    });

    return symbol;
  }

  public createPolylineSymbol(
    widthSymbol: number,
    backgroundColorSymbol: string,
    opacitySymbol: number,
    styleLineSymbol: any = 'solid'
  ) {
    const backgroundColor = ColorUtils.alpha(
      backgroundColorSymbol,
      opacitySymbol
    ).rgba();

    const symbol = new SimpleLineSymbol({
      color: backgroundColor,
      width: widthSymbol,
      style: styleLineSymbol,
    });

    return symbol;
  }

  public createTextSymbol(
    phraseSymbol: string,
    fontColorSymbol: string,
    fontSizeSymbol: string,
    opacitySymbol: number
  ) {
    const fontColor = ColorUtils.alpha(fontColorSymbol, opacitySymbol).rgba();
    const symbol = new TextSymbol({
      text: phraseSymbol,
      color: fontColor,
      font: {
        size: fontSizeSymbol,
      },
    });

    return symbol;
  }

  public createEllipseSymbol(
    backgroundColorSymbol: string,
    outlineColorSymbol: string,
    opacitySymbol: number,
    outlineWidthSymbol: number
  ) {
    const outlineColor = ColorUtils.alpha(
      outlineColorSymbol,
      opacitySymbol
    ).rgba();
    const backgroundColor = ColorUtils.alpha(
      backgroundColorSymbol,
      opacitySymbol
    ).rgba();

    const symbol = new SimpleFillSymbol({
      color: backgroundColor,
      outline: {
        color: outlineColor,
        width: outlineWidthSymbol,
        cap: 'round',
        join: 'round',
      },
    });

    return symbol;
  }

  public getSymbolFromPortal(): Observable<
    { symbol: __esri.Symbol; name: string; item: any }[]
  > {
    const IDmarkerBasics = '5def7ec784d745a8999935f6fd9ab29d';
    const symbolNameExpectedList = [
      'Circle',
      'Square',
      'Diamond',
      'Cross',
      'X',
    ];

    const url = `${this.appConfigService.portalUrl}/sharing/rest/content/items/${IDmarkerBasics}/data`;

    return this.cacheService.getCached({
      path: url,
      lifetimeSeconds: 0,
      source: () => {
        return this.http.get<any[]>(url).pipe(
          map((res) => {
            const symbolsFiltered = res?.filter(
              (item) =>
                !(item?.contentType === 'image/png') &&
                symbolNameExpectedList.includes(item?.name)
            );
            return (
              symbolsFiltered?.map((item) => {
                const symbol = symbolJsonUtils.fromJSON(item);

                return { symbol, name: item.name, item };
              }) ?? []
            );
          })
        );
      },
    });
  }

  public createImagePreviewForSymbol({ symbol }) {
    return defer(() => {
      return from(EsriUtils.renderSymbolPreviewImageUrl(symbol)).pipe(
        map((icon) => ({ symbol, icon, useAs: 'svg' }))
      );
    });
  }
}
